import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import type { EnvironmentView, LearnerViewInPlay, ScenePlan } from "@pushback-and-chaos/contracts";
import { ARRIVAL, CASEBOOK, PLAY, PROPOSED } from "../design/copy.js";
import { FreeTextInput } from "../ui/input.js";
import { placeOnStack, useDeskCards, type DeskDocument } from "../documents/desk.js";
import type { DeskPlace } from "../documents/landing.js";
import type { ConnectionState, TurnFailure } from "../data/turn-machine.js";
import type { ThreadEntry } from "../data/adapt.js";
import type { TranscribeFn } from "../ui/dictation.js";
import { useNarrationAudio, type FetchAudioFn, type NarrationAudioControl } from "../ui/narration-audio.js";
import { Pager, PanelHeading } from "./storyteller/chrome.js";
import { ScrollPage, newestOf, splitPages, useReadingPlace, type Page, type PageItem } from "./storyteller/pages.js";
import type { BookChapter, BookScene } from "./storyteller/casebook-atoms.js";
import { storyAtoms } from "./storyteller/narration.js";
import { ScenePanel, type SceneHero } from "./storyteller/scene.js";
import { PrologueBlocks } from "./beat.js";
import { DeskPanel } from "./storyteller/desk-panel.js";
import { DocumentWindow, NotesWindow, defaultPlace, type SurfaceSize, type WindowPlace } from "./storyteller/paper-window.js";

/**
 * THE PLAY SURFACE — "The Storyteller", layout 04 (2026-09-17).
 *
 * Three persistent regions in one viewport and nothing else:
 *
 *   +-------------------+-------------------------+
 *   |                   |          SCENE          |
 *   |   CONVERSATION    +-------------------------+
 *   |  [reply composer] |      DOCUMENT DESK      |
 *   +-------------------+-------------------------+
 *          43%                      57%
 *
 * with the right column split 64:36. The conversation leads on the left, full
 * height, because reading and answering is what a learner does here; the
 * world and the evidence sit beside it rather than above it.
 *
 * PAGES THAT SCROLL (2026-10-02). The story is a book whose pages are
 * chapters — a new page at every new in-game day (since 2026-10-29), or once
 * a page holds a chapter's worth — and each page scrolls
 * (storyteller/pages.tsx). A page is decided
 * from the content alone, so nothing ever moves between pages; a paper in
 * hand scrolls as one sheet; the composer grows with the writing. The whole
 * thread and the whole of every document stay reachable, and `overflow:
 * hidden` on the panels is containment, never a way of hiding text.
 *
 * TYPE-LEVEL LAW 2 (build brief section 2). `InPlayProps` below is built ONLY
 * from `LearnerViewInPlay` plus plain diegetic strings pulled off the SSE
 * envelope union. It has no type-level access to `MechanicalState`, to
 * `CheckSpec`, or to a roll — those types are not imported in this file or
 * anything it renders, and `play.leak.test.tsx` asserts
 * `ForbiddenKeys<InPlayProps>` resolves to `never`, which fails to compile
 * the moment any number, Band, Position or Effect is added to this surface.
 * `EnvironmentView` and `ScenePlan` join it here, because the scene moved
 * into the composition from the shell above it; neither carries a mechanic —
 * a diegetic label, an authored description, a wall clock, and where people
 * are standing.
 */
export interface InPlayProps {
  readonly sessionId: string;
  readonly view: LearnerViewInPlay;
  /**
   * The story thread (fix-phase 3a): per-turn learner input + what came back,
   * fetched from the server's event log. When non-empty it replaces the bare
   * narration list — a story the learner reads back, not a stateless response
   * box. Paging to the first turn always works because the server, not client
   * state, is the source.
   */
  readonly thread: readonly ThreadEntry[];
  /** The input currently being processed, echoed at the end of the thread while the turn runs. */
  readonly pendingText: string | null;
  readonly hypotheses: readonly string[];
  /** The living notes: established findings and open questions, prose in the learner's own voice. */
  readonly notes: { readonly established: readonly string[]; readonly open: readonly string[] };
  /** `NPC_STATE_CHANGED.tell` — authored diegetic text, never a disposition label. */
  readonly npcTells: readonly string[];
  /** `STATUS_CHANGED.status` — authored diegetic text ("in the room, presenting"). */
  readonly statuses: readonly string[];
  readonly connection: ConnectionState;
  readonly resumedFromTurn: number | null;
  readonly busy: boolean;
  readonly failure: TurnFailure | null;
  readonly blockedReason: string | null;
  readonly canRecommend: boolean;
  /**
   * The document the case declares as the learner's own note-to-self
   * (`orientation_note_doc_id`). Opened once, on first entry, and never again.
   * Null while the case opening has not loaded — the note simply opens on a
   * later render, which is fine: it is orientation, not a gate.
   */
  readonly orientationNoteDocId: string | null;
  /**
   * The mentor's opening ask (onboarding pass, 2026-09-13), already in his
   * voice with his name in it, rendered under the authored opening. Null for a
   * case without a mentor.
   */
  readonly mentorAsk: string | null;
  /** The composer's placeholder for this turn: the first three turns ask for the shape of a move, then the standing line returns. */
  readonly composerPlaceholder: string;
  /**
   * THE SCENE (ADR 0009 / 0014), now a region of this composition rather than
   * a band above it. Still the server's own view and the turn's own plan; this
   * surface hands them to `EnvironmentSection` and decides nothing about them.
   */
  readonly environment: EnvironmentView | null;
  readonly environmentPlan: ScenePlan | null;
  /**
   * The voice (ADR 0011). `dictate` is null where this deployment has no
   * dictation; `narrationAudio` is null where it has no narration voice — the
   * controls show as "not yet." then. `readAloud` is the learner's setting:
   * voice each turn as it lands.
   */
  readonly dictate: TranscribeFn | null;
  readonly narrationAudio: FetchAudioFn | null;
  readonly readAloud: boolean;
  /**
   * THE BOOK'S FIRST PAGES (2026-09-22). The case's title and its authored
   * prologue, read in the conversation panel before the story: page one is
   * the title, the next pages the prologue, and the composer appears only
   * once the reader has turned into the story. Null while the case opening
   * has not loaded, when the book simply opens on the story. Strings only —
   * this surface may not be handed a number (play.leak.test.tsx).
   */
  readonly prologue: PlayPrologue | null;
  /**
   * THE CASEBOOK (2026-09-24): the same surface, closed. Present only when a
   * finished engagement is read back as a book: the composer is gone, the
   * book opens on its title page, its pages end with the closing chapters, and
   * the scene and the desk follow the page being read. Functions and strings
   * only, like everything else here.
   */
  readonly book?: PlayBook | null;
  readonly submit: (text: string) => void;
  readonly retry: () => void;
  readonly openSettings: () => void;
  readonly openRecommend: () => void;
}

/** A finished engagement, as the play surface reads it (see `book` above). */
export interface PlayBook {
  readonly items: () => readonly PageItem[];
  readonly chapters: () => readonly BookChapter[];
  /** The atom the review opens with, where "the end." turns to. */
  readonly reviewKey: string;
  readonly sceneFor: (atomKey: string | null) => BookScene;
  readonly documentsFor: (atomKey: string | null) => LearnerViewInPlay["documents"];
  readonly allDocuments: () => LearnerViewInPlay["documents"];
  /** "presented · 23 September", on the title page; the met date for the arrival book. */
  readonly titleNote: string;
  /**
   * WHICH BOOK (2026-10-11). A closed engagement unless it says otherwise: the
   * running head and the foot say so, and the doors are the tape and the case
   * again. The arrival book is the record's first book and not an engagement:
   * it has its own words, no tape, no case to try again, and one door back to
   * the record. A door is drawn only where it leads somewhere.
   */
  readonly kind?: "engagement" | "arrival";
  readonly onTape?: (() => void) | undefined;
  readonly onTryAgain?: (() => void) | undefined;
  readonly onBack?: (() => void) | undefined;
}

/** The case as its title page and prologue say it. */
export interface PlayPrologue {
  readonly slug: string;
  readonly client: string;
  readonly industry: string;
  readonly question: string;
  readonly pages: readonly PrologueChapter[];
}

/** One authored prologue page. `pageRef` is the bible's own page number, as a string, for the hero lookup. */
export interface PrologueChapter {
  readonly pageRef: string;
  readonly eyebrow: string;
  readonly title: string;
  readonly heroImageSlot: string;
  readonly blocks: readonly { readonly kind: "paragraph" | "dialogue" | "learner_dialogue"; readonly text: string }[];
}

/**
 * How many pages come before the story: the title page and every prologue
 * page. A closed book always has its title page (2026-10-11): the arrival
 * book has no prologue, and a casebook whose opening could not be read still
 * opens on its title and its ending note rather than in the middle of the
 * story. A live run without its opening opens on the story, as before.
 */
function prefaceCount(prologue: PlayPrologue | null, closed: boolean): number {
  if (prologue === null) return 0;
  return prologue.pages.length === 0 && !closed ? 0 : prologue.pages.length + 1;
}

/**
 * THE ARRANGEMENT (organization pass, 2026-08-28; desk pass, 2026-09-03;
 * re-sited for the Storyteller layout, 2026-09-17): where the learner has put
 * things, and what they have typed and not yet sent. Pure presentation,
 * persisted per session in localStorage only — which documents EXIST stays
 * server truth; how they are arranged is the learner's own business and never
 * leaves their browser (Law 5).
 */
interface Arrangement {
  /**
   * `desk` is marked as fractions of the desk's free range. An arrangement
   * without the mark predates that and its desk entries are dropped — every
   * card simply re-lands, identically, from its seed.
   */
  readonly deskUnits?: "fraction";
  /**
   * Where the LEARNER put cards, by document id. A card with no entry sits
   * where it landed (landing.ts, pure from the delivery order), so nothing
   * here is derived from what else is open.
   */
  readonly desk: Readonly<Record<string, DeskPlace>>;
  /** Cards the learner has put down, oldest first — each above every never-moved card. */
  readonly raised: readonly string[];
  /** Documents this browser has shown on the desk; a document not here slides in on arrival. */
  readonly seen: readonly string[];
  /**
   * Papers in hand, oldest first: a document id and where its window sits, as
   * fractions of the play surface's free range. `windows: "fraction"` marks
   * that; an arrangement without it held REM OFFSETS into a narrative pane
   * that no longer exists, and its positions are dropped while its documents
   * are kept — a paper reopens at a default corner rather than off-screen.
   */
  readonly windows?: "fraction";
  readonly open: readonly OpenPaper[];
  /** The notebook is a paper like any other; null means it is not in hand. */
  readonly notes: WindowPlace | null;
  /**
   * What the learner has typed and not yet sent. Enter makes a line break
   * here, so an answer is routinely a paragraph that took minutes to write,
   * and a refresh used to destroy it in silence.
   */
  readonly draft: string;
  /**
   * True once the note-to-self has been opened on first entry. The note opens
   * ONCE and never again: a tutorial that reappears is a tutorial that is
   * being ignored, and this one is meant to be read exactly when it is useful.
   */
  readonly orientationNoteShown: boolean;
}

/** An open document: its id and where its window sits. */
interface OpenPaper {
  readonly id: string;
  readonly pos: WindowPlace;
}

const EMPTY_ARRANGEMENT: Arrangement = {
  deskUnits: "fraction",
  windows: "fraction",
  desk: {},
  raised: [],
  seen: [],
  open: [],
  notes: null,
  draft: "",
  orientationNoteShown: false,
};

function loadArrangement(sessionId: string): Arrangement {
  try {
    const raw = localStorage.getItem(`pc-arrangement:${sessionId}`);
    if (raw === null) return EMPTY_ARRANGEMENT;
    const parsed = JSON.parse(raw) as Partial<Arrangement> & { open?: unknown; notesOpen?: unknown; order?: unknown; active?: unknown };
    /*
     * MIGRATION, across two earlier shapes, and the rule for both is the same:
     * KEEP WHAT THE LEARNER AUTHORED, DROP WHAT THE LAYOUT OWNED.
     *
     *   - the first shape held `open: [{ id, pos }]` with `pos` in rem inside
     *     a narrative pane; those offsets would reopen a paper off-screen
     *   - the second held `open: ["id"]` and an `active` id, for readers that
     *     filled the desk
     *
     * Either way the DOCUMENTS stay in hand and the DRAFT is untouched; only
     * the coordinates are replaced, by a default corner each.
     */
    const ids = Array.isArray(parsed.open)
      ? parsed.open.flatMap((entry): readonly { id: string; pos: WindowPlace | null }[] => {
          if (typeof entry === "string") return [{ id: entry, pos: null }];
          if (typeof entry !== "object" || entry === null) return [];
          const id: unknown = (entry as { id?: unknown }).id;
          if (typeof id !== "string") return [];
          const pos: unknown = (entry as { pos?: unknown }).pos;
          const place = parsed.windows === "fraction" ? asPlace(pos) : null;
          return [{ id, pos: place }];
        })
      : [];
    const open: OpenPaper[] = ids.map((entry, index) => ({ id: entry.id, pos: entry.pos ?? defaultPlace(index) }));

    const merged: Arrangement = { ...EMPTY_ARRANGEMENT, ...parsed, open, windows: "fraction" };
    const notes = parsed.windows === "fraction" ? asPlace(parsed.notes) : parsed.notesOpen === true ? defaultPlace(open.length) : null;
    return {
      ...merged,
      notes,
      desk: parsed.deskUnits === "fraction" && typeof parsed.desk === "object" && parsed.desk !== null ? merged.desk : {},
      raised: parsed.deskUnits === "fraction" && Array.isArray(parsed.raised) ? merged.raised : [],
      deskUnits: "fraction",
      seen: Array.isArray(parsed.seen) ? merged.seen : [],
    };
  } catch {
    return EMPTY_ARRANGEMENT;
  }
}

/** A stored value that really is a place, or null. Never trusted on the way in. */
function asPlace(value: unknown): WindowPlace | null {
  if (typeof value !== "object" || value === null) return null;
  const { fx, fy } = value as { fx?: unknown; fy?: unknown };
  if (typeof fx !== "number" || typeof fy !== "number" || !Number.isFinite(fx) || !Number.isFinite(fy)) return null;
  return { fx: Math.min(Math.max(fx, 0), 1), fy: Math.min(Math.max(fy, 0), 1) };
}

function saveArrangement(sessionId: string, a: Arrangement): void {
  try {
    localStorage.setItem(`pc-arrangement:${sessionId}`, JSON.stringify(a));
  } catch {
    /* storage unavailable: the arrangement is a convenience, not state */
  }
}

export function PlaySurface(props: InPlayProps): ReactNode {
  const [arrangement, setArrangement] = useState<Arrangement>(() => loadArrangement(props.sessionId));
  const commit = useCallback(
    (update: (a: Arrangement) => Arrangement) => {
      setArrangement((a) => {
        const next = update(a);
        saveArrangement(props.sessionId, next);
        return next;
      });
    },
    [props.sessionId],
  );

  const book = props.book ?? null;
  /** The first atom of the page being read in a closed book, so its room and its desk can follow it. */
  const [readingKey, setReadingKey] = useState<string | null>(null);
  const deskSource = useMemo(() => (book === null ? props.view.documents : book.allDocuments()), [book, props.view.documents]);
  const allCards = useDeskCards(props.sessionId, deskSource);

  /**
   * The play surface itself — the rectangle a window's fractional place is
   * resolved against, and the walls it may not cross. Measured rather than
   * assumed, so a resize moves every open paper with the layout.
   */
  const surfaceRef = useRef<HTMLDivElement | null>(null);
  const [surface, setSurface] = useState<SurfaceSize>({ width: 0, height: 0 });
  useEffect(() => {
    const element = surfaceRef.current;
    if (element === null || typeof ResizeObserver === "undefined") return;
    const read = (): void => {
      const rect = element.getBoundingClientRect();
      setSurface((previous) =>
        Math.abs(previous.width - rect.width) < 0.5 && Math.abs(previous.height - rect.height) < 0.5 ? previous : { width: rect.width, height: rect.height },
      );
    };
    read();
    const observer = new ResizeObserver(read);
    observer.observe(element);
    return () => { observer.disconnect(); };
  }, []);

  /** These documents have been shown on the desk; they will not slide in again in this browser. */
  const onSeen = useCallback(
    (ids: readonly string[]) => { commit((a) => ({ ...a, seen: [...a.seen, ...ids.filter((id) => !a.seen.includes(id))] })); },
    [commit],
  );

  /** The learner put a card down: it sits there now, above everything that was never moved. */
  const onPlace = useCallback(
    (id: string, place: DeskPlace) => {
      commit((a) => ({ ...a, desk: { ...a.desk, [id]: place }, raised: [...a.raised.filter((r) => r !== id), id] }));
    },
    [commit],
  );

  /** A point on the screen -> a place on the play surface, for a window of this size. */
  const placeAt = useCallback((clientX: number, clientY: number, size: { readonly width: number; readonly height: number }): WindowPlace => {
    const rect = surfaceRef.current?.getBoundingClientRect();
    if (rect === undefined) return defaultPlace(0);
    const freeX = Math.max(rect.width - size.width, 0);
    const freeY = Math.max(rect.height - size.height, 0);
    const clamp = (value: number): number => Math.min(Math.max(value, 0), 1);
    return {
      fx: freeX > 0 ? clamp((clientX - rect.left - size.width / 2) / freeX) : 0,
      fy: freeY > 0 ? clamp((clientY - rect.top - 18) / freeY) : 0,
    };
  }, []);

  /** Opening, or re-opening, brings a paper to the front of the pile. */
  const openDoc = useCallback(
    (id: string, pos: WindowPlace | null) => {
      commit((a) => ({
        ...a,
        open: [...a.open.filter((d) => d.id !== id), { id, pos: pos ?? a.open.find((d) => d.id === id)?.pos ?? defaultPlace(a.open.length) }],
      }));
    },
    [commit],
  );

  const onOpen = useCallback((doc: DeskDocument) => { openDoc(doc.id, null); }, [openDoc]);
  /**
   * A card let go OFF the desk — over the conversation or over the scene —
   * opens there, at the point it was let go. The window's own size is not
   * known until it is drawn, so the drop is turned into a place with a
   * nominal size; the window's measurement corrects it on the next frame.
   */
  const onOpenAt = useCallback(
    (doc: DeskDocument, clientX: number, clientY: number) => {
      openDoc(doc.id, placeAt(clientX, clientY, { width: 420, height: 260 }));
    },
    [openDoc, placeAt],
  );

  const moveWindow = useCallback(
    (id: string, pos: WindowPlace) => { commit((a) => ({ ...a, open: a.open.map((d) => (d.id === id ? { id, pos } : d)) })); },
    [commit],
  );
  const close = useCallback((id: string) => { commit((a) => ({ ...a, open: a.open.filter((d) => d.id !== id) })); }, [commit]);
  const front = useCallback(
    (id: string) => { commit((a) => ({ ...a, open: [...a.open.filter((d) => d.id !== id), ...a.open.filter((d) => d.id === id)] })); },
    [commit],
  );

  /** A window carried back onto the desk becomes a card again, where it was let go. */
  const toDesk = useCallback(
    (id: string, clientX: number, clientY: number) => {
      const stack = document.querySelector('[data-region="desk-stack"]');
      const place: DeskPlace = stack instanceof HTMLElement ? placeOnStack(stack, clientX - 60, clientY - 30) : { fx: 0, fy: 0 };
      commit((a) => ({ ...a, open: a.open.filter((d) => d.id !== id), desk: { ...a.desk, [id]: place }, raised: [...a.raised.filter((r) => r !== id), id] }));
    },
    [commit],
  );

  const openNotes = useCallback(() => { commit((a) => ({ ...a, notes: a.notes ?? defaultPlace(a.open.length) })); }, [commit]);
  const closeNotes = useCallback(() => { commit((a) => ({ ...a, notes: null })); }, [commit]);

  /*
   * WHERE THE BOOK OPENS. A run that has not yet heard from the learner opens
   * on its title page; a run under way opens on the story, at its newest
   * page, as it always did. Decided from the thread, not from this page load,
   * and left alone the moment the reader turns a page themselves — the
   * opening may arrive after the thread, and must not throw a reader who has
   * already begun back to the title.
   */
  const preface = prefaceCount(props.prologue, book !== null);
  const fresh = props.pendingText === null && !props.thread.some((entry) => entry.learnerText !== null);
  /*
   * DERIVED, NOT SYNCHRONISED. `turnedTo` is where the reader last turned, or
   * undefined while they have not touched the book; until they do, the open
   * page follows the props in the SAME render. An effect that copied the
   * props into state ran one render behind them, and the orientation note —
   * which waits for the story — read the stale page and opened over the
   * title page (seen live, 2026-09-22, twice).
   */
  const [turnedTo, setTurnedTo] = useState<number | null | undefined>(undefined);
  // A closed book always opens on its title page.
  const prefacePage: number | null = turnedTo === undefined ? (preface > 0 && (fresh || book !== null) ? 0 : null) : turnedTo;
  const onPrefacePage = useCallback((next: number | null) => {
    setTurnedTo(next);
  }, []);

  const hero = sceneHero(props.prologue, prefacePage);
  const bookScene = book !== null && prefacePage === null ? book.sceneFor(readingKey) : null;
  const visibleIds = useMemo(() => (book === null ? null : new Set(book.documentsFor(prefacePage !== null ? null : readingKey).map((doc) => doc.id))), [book, prefacePage, readingKey]);
  const cards = useMemo(() => (allCards === null || visibleIds === null ? allCards : allCards.filter((card) => visibleIds.has(card.id))), [allCards, visibleIds]);

  /**
   * OPEN THE NOTE ONCE, on first entry to the case.
   *
   * It was authored well and nobody read it. Opening it is the difference
   * between a learner who knows they can just say what they think and one who
   * spends three turns discovering it. Waits for the document to actually be
   * on the desk rather than firing on mount — at first render the session view
   * may still be arriving, and opening an id that is not there would burn the
   * once-only flag on nothing.
   */
  const orientationNoteDocId = props.orientationNoteDocId;
  useEffect(() => {
    if (orientationNoteDocId === null || book !== null) return;
    // Not over the title page or the prologue (2026-09-22): the note is
    // orientation for the first move, and it opens when the reader turns
    // into the story, where the desk it lies on is in front of them. It
    // also waits for the book to KNOW whether it has first pages — the
    // opening can arrive a render after the documents, and a note opened
    // in that gap would sit over the title page (seen live, 2026-09-22).
    if (prefacePage !== null || props.prologue === null) return;
    if (!props.view.documents.some((doc) => doc.id === orientationNoteDocId)) return;
    commit((a) =>
      a.orientationNoteShown
        ? a
        : {
            ...a,
            orientationNoteShown: true,
            open: [...a.open.filter((d) => d.id !== orientationNoteDocId), { id: orientationNoteDocId, pos: defaultPlace(a.open.length) }],
          },
    );
  }, [orientationNoteDocId, props.view.documents, commit, prefacePage, props.prologue, book]);

  /**
   * READ ALOUD, when asked. A turn that just landed is voiced once, the moment
   * it appears; the thread as loaded on arrival is not — coming back to a
   * session must not start reading its whole history. The first key seen is
   * therefore recorded and skipped, and only a CHANGE of last entry plays.
   */
  const voice = useNarrationAudio(props.narrationAudio);
  const lastThreadKey = useRef<string | null>(null);
  const { readAloud, narrationAudio, thread } = props;
  const playVoice = voice.play;
  useEffect(() => {
    const last = thread.at(-1);
    const key = last?.key ?? null;
    const previous = lastThreadKey.current;
    lastThreadKey.current = key;
    if (previous === null || key === null || key === previous) return;
    if (!readAloud || narrationAudio === null || last === undefined || last.narration.length === 0) return;
    playVoice(key);
  }, [thread, readAloud, narrationAudio, playVoice]);

  // Escape puts down the paper on top; with none open, it puts down the notes.
  // Never while typing in the composer — that is the learner's own text and
  // Escape there means nothing to us.
  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent): void => {
      if (event.key !== "Escape" || event.defaultPrevented) return;
      const target = event.target;
      if (target instanceof HTMLTextAreaElement || target instanceof HTMLInputElement) return;
      commit((a) => {
        if (a.open.length > 0) return { ...a, open: a.open.slice(0, -1) };
        if (a.notes !== null) return { ...a, notes: null };
        return a;
      });
    };
    window.addEventListener("keydown", onKeyDown);
    return () => { window.removeEventListener("keydown", onKeyDown); };
  }, [commit]);

  const openIds = arrangement.open.map((d) => d.id);

  return (
    <div className="pc-storyteller">
      {/* The play surface is the positioned ancestor of every open paper, so a
          window may lie over the conversation OR over the scene and is walled
          by this rectangle and nothing smaller. */}
      <div className="pcs-grid" ref={surfaceRef}>
        <ConversationPanel
          {...props}
          voice={props.narrationAudio === null ? null : voice}
          draftText={arrangement.draft}
          onDraftChange={(text) => { commit((a) => ({ ...a, draft: text })); }}
          prefacePage={prefacePage}
          onPrefacePage={onPrefacePage}
          onReadingKey={setReadingKey}
        />
        <ScenePanel
          environment={bookScene === null ? props.environment : bookScene.environment}
          plan={bookScene === null ? props.environmentPlan : bookScene.plan}
          hero={hero}
          titleCover={(prefacePage === 0 || bookScene?.cover === true) && props.prologue !== null ? { slug: props.prologue.slug, client: props.prologue.client } : null}
        />
        <DeskPanel
          sessionId={props.sessionId}
          cards={cards}
          openIds={openIds}
          places={arrangement.desk}
          raisedIds={arrangement.raised}
          seenIds={arrangement.seen}
          notesInHand={arrangement.notes !== null}
          onOpen={onOpen}
          onOpenAt={onOpenAt}
          onPlace={onPlace}
          onSeen={onSeen}
          onOpenNotes={openNotes}
        />

        {/* The papers in hand. Later in the list is nearer the front. */}
        {arrangement.open.map((doc, index) => {
          const card = cards?.find((c) => c.id === doc.id);
          return card === undefined ? null : (
            <DocumentWindow
              key={doc.id}
              sessionId={props.sessionId}
              card={card}
              place={doc.pos}
              surface={surface}
              zIndex={30 + index}
              onFront={() => { front(doc.id); }}
              onMove={(pos) => { moveWindow(doc.id, pos); }}
              onToDesk={(x, y) => { toDesk(doc.id, x, y); }}
              onClose={() => { close(doc.id); }}
            />
          );
        })}

        {arrangement.notes === null ? null : (
          <NotesWindow
            notes={props.notes}
            hypotheses={props.hypotheses}
            threads={props.view.open_threads}
            place={arrangement.notes}
            surface={surface}
            zIndex={30 + arrangement.open.length}
            onFront={() => undefined}
            onMove={(pos) => { commit((a) => ({ ...a, notes: pos })); }}
            // The notebook has no card form; carried back to the desk it is
            // simply put away, which is what the desk's own control reopens.
            onToDesk={closeNotes}
            onClose={closeNotes}
          />
        )}
      </div>
    </div>
  );
}

/**
 * The hero the scene shows for a prologue page: each page shows its own.
 * Null on the title page (which shows the case's cover) and in the story.
 */
function sceneHero(prologue: PlayPrologue | null, prefacePage: number | null): SceneHero | null {
  if (prologue === null || prefacePage === null || prefacePage === 0) return null;
  const page = prologue.pages[prefacePage - 1];
  if (page === undefined) return null;
  const index = Number.parseInt(page.pageRef, 10);
  return {
    caseSlug: prologue.slug,
    pageIndex: Number.isFinite(index) ? index : 1,
    slot: page.heroImageSlot,
    caption: PLAY.prologueCaption,
    foot: page.title,
  };
}

/**
 * The left column: the case's running head, the book, its page control, and
 * the composer pinned at the foot.
 *
 * THE BOOK (2026-09-22) has pages before the story: the title page and the
 * prologue. They are drawn over the story's reading area, which goes on
 * underneath, and the one page control counts them all: "1 / 9" is the
 * title, the prologue runs to the fourth page, and the story begins on the
 * fifth, where the composer appears. Turning back into the prologue puts the
 * composer away again; the first pages are always there at the beginning of
 * the story.
 */
const EMPTY_PAGE: Page = { key: "", items: [] };

function ConversationPanel(
  props: InPlayProps & {
    readonly voice: NarrationAudioControl | null;
    readonly draftText: string;
    readonly onDraftChange: (text: string) => void;
    /** Which preface page is open, or null when the reader is in the story. */
    readonly prefacePage: number | null;
    readonly onPrefacePage: (next: number | null) => void;
    /** Reports the first atom of the story page being read (null in the preface). */
    readonly onReadingKey: (key: string | null) => void;
  },
): ReactNode {
  const book = props.book ?? null;
  const arrival = book?.kind === "arrival";
  const preface = prefaceCount(props.prologue, book !== null);
  const inPreface = props.prefacePage !== null && preface > 0;
  const items = useMemo(
    () =>
      book !== null ? book.items() : storyAtoms({
        view: props.view,
        thread: props.thread,
        pendingText: props.pendingText,
        mentorAsk: props.mentorAsk,
        npcTells: props.npcTells,
        statuses: props.statuses,
        busy: props.busy,
        voice: props.voice,
      }),
    [book, props.view, props.thread, props.pendingText, props.mentorAsk, props.npcTells, props.statuses, props.busy, props.voice],
  );
  // The pages, from the content alone; the newest exchange, which a following
  // reader is carried to; and where the reader is. A closed book is read from
  // its beginning; the live story follows its newest turn.
  const pages = useMemo(() => splitPages(items), [items]);
  const newest = useMemo(() => newestOf(items), [items]);
  const place = useReadingPlace(pages.length, book === null ? "latest" : "start");
  const firstKeys = useMemo(() => pages.map((page) => page.key), [pages]);

  /*
   * ONE PAGE CONTROL over the whole book. The preface pages come first, then
   * the story's own measured pages; the control's count is the honest sum,
   * and a turn from the story's first page goes back into the prologue.
   */
  const bookPage = inPreface ? (props.prefacePage ?? 0) : preface + place.page;
  const bookCount = preface + place.count;
  const { onPrefacePage } = props;
  const setPlacePage = place.setPage;
  const toLatest = place.toLatest;
  const turnTo = useCallback(
    (next: number) => {
      const clamped = Math.min(Math.max(next, 0), Math.max(bookCount - 1, 0));
      if (clamped < preface) {
        onPrefacePage(clamped);
        return;
      }
      onPrefacePage(null);
      setPlacePage(clamped - preface);
    },
    [bookCount, preface, onPrefacePage, setPlacePage],
  );
  const toNewest = useCallback(() => {
    onPrefacePage(null);
    toLatest();
  }, [onPrefacePage, toLatest]);
  // Sending a line turns the book to where the answer will land, wherever
  // the reader had turned back to: they asked, and this is where it is said.
  const { pendingText } = props;
  useEffect(() => { if (pendingText !== null) toNewest(); }, [pendingText, toNewest]);

  // THE CLOSED BOOK'S PLACE: the atom at the top of the page being read, as the
  // reader scrolls, reported so the scene and the desk follow the reading; the
  // contents page and "the end." turn to chapters.
  const [topKey, setTopKey] = useState<string | null>(null);
  const readingKey = inPreface ? null : (topKey ?? firstKeys[place.page] ?? null);
  const { onReadingKey } = props;
  useEffect(() => { if (book !== null) onReadingKey(readingKey); }, [book, onReadingKey, readingKey]);
  const [contentsOpen, setContentsOpen] = useState(false);
  const pageOfKey = useCallback((key: string): number | null => {
    const index = firstKeys.indexOf(key);
    return index === -1 ? null : preface + index;
  }, [firstKeys, preface]);
  const toKey = useCallback((key: string) => {
    const page = pageOfKey(key);
    if (page !== null) turnTo(page);
  }, [pageOfKey, turnTo]);

  const pager = (
    <Pager
      page={bookPage}
      count={bookCount}
      onPage={turnTo}
      what="story"
      end={
        book !== null ? (
          // Codex (2026-09-24): no "the end." on the last page; elsewhere it reaches the review.
          bookPage >= bookCount - 1 ? null : (
            <button type="button" onClick={() => { toKey(book.reviewKey); }}>
              {CASEBOOK.theEnd}
            </button>
          )
        ) : (
          <button type="button" onClick={toNewest} disabled={bookPage >= bookCount - 1}>
            Latest
          </button>
        )
      }
    />
  );

  return (
    <section className="pcs-panel pcs-narration" aria-label="The conversation">
      {/*
        * THE RUNNING HEAD (2026-09-22): the case, compact, the way a chapter
        * title runs along the top of a page — the client's name as the
        * panel's tracked label, the industry quiet beside it. The menu that
        * used to sit here is the site header's now.
        */}
      {props.prologue === null ? <div className="pcs-heading" /> : <PanelHeading label={props.prologue.client}>{book === null ? props.prologue.industry : arrival ? ARRIVAL.runningHead : CASEBOOK.runningHead}</PanelHeading>}

      {/* The body: the story, its page control and the composer, with a
          preface page drawn OVER all three while one is open. The composer
          keeps its room underneath (visibility, not display), so the story's
          reading area is the same size on either side of the turn into it. */}
      <div className="pcs-body">
      <ScrollPage
        page={pages[place.page] ?? EMPTY_PAGE}
        label="The conversation"
        live
        newest={newest}
        following={place.following}
        latestTick={place.latestTick}
        onTopKey={book === null ? undefined : setTopKey}
      />

      {pager}

      {inPreface && props.prologue !== null ? (
        <PrefacePage prologue={props.prologue} page={props.prefacePage ?? 0} pager={pager} onTurn={() => { turnTo(bookPage + 1); }} titleNote={book?.titleNote ?? null} titleEyebrow={arrival ? ARRIVAL.titleEyebrow : PLAY.titleEyebrow} />
      ) : null}

      {book !== null && contentsOpen ? (
        <BookContents
          eyebrow={arrival ? ARRIVAL.contentsEyebrow : CASEBOOK.contentsEyebrow}
          chapters={book.chapters()}
          hasPreface={preface > 0}
          hasPrologue={preface > 1}
          pageOfKey={pageOfKey}
          currentPage={bookPage}
          onTurn={(page) => { setContentsOpen(false); turnTo(page); }}
          onClose={() => { setContentsOpen(false); }}
        />
      ) : null}

      {book !== null ? (
        <div className="pcs-book-foot" data-region="book-foot">
          <p className="pcs-book-closed">{arrival ? ARRIVAL.closedLine : CASEBOOK.closedLine}</p>
          <div className="pcs-book-doors">
            <button type="button" onClick={() => { setContentsOpen((open) => !open); }} aria-expanded={contentsOpen}>{CASEBOOK.contents}</button>
            {book.onTape === undefined ? null : <button type="button" onClick={book.onTape}>{CASEBOOK.tape}</button>}
            {book.onTryAgain === undefined ? null : <button type="button" onClick={book.onTryAgain}>{CASEBOOK.tryAgain}</button>}
            {book.onBack === undefined ? null : <button type="button" onClick={book.onBack}>{ARRIVAL.back}</button>}
          </div>
        </div>
      ) : (
      <div className={inPreface ? "pcs-composer pcs-composer-away" : "pcs-composer"} aria-hidden={inPreface ? "true" : undefined}>
        <ConnectionLine connection={props.connection} resumedFromTurn={props.resumedFromTurn} failure={props.failure} onRetry={props.retry} />
        <FreeTextInput
          placeholder={props.composerPlaceholder}
          disabled={props.busy || props.blockedReason !== null}
          disabledReason={props.blockedReason ?? (props.busy ? "Working." : undefined)}
          onSubmit={props.submit}
          onOpenSettings={props.openSettings}
          draft={props.draftText}
          onDraftChange={props.onDraftChange}
          dictate={props.dictate}
          compact
          extraControl={
            <button
              type="button"
              onClick={props.openRecommend}
              disabled={!props.canRecommend}
              className={["cursor-pointer border-0 bg-transparent p-0 font-sans text-micro", props.canRecommend ? "text-ink hover:bg-marker" : "cursor-not-allowed text-ink-faint"].join(" ")}
            >
              {PROPOSED.submitRecommendation}
            </button>
          }
        />
      </div>
      )}
      </div>
    </section>
  );
}

/**
 * THE CONTENTS (the casebook, 2026-09-24): drawn over the reading area like a
 * preface page. Each chapter turns to the page it starts on; the chapter the
 * reader was in says so.
 */
function BookContents({
  eyebrow,
  chapters,
  hasPreface,
  hasPrologue,
  pageOfKey,
  currentPage,
  onTurn,
  onClose,
}: {
  /** "The casebook", or the arrival book's own name. */
  readonly eyebrow: string;
  readonly chapters: readonly BookChapter[];
  readonly hasPreface: boolean;
  readonly hasPrologue: boolean;
  readonly pageOfKey: (key: string) => number | null;
  readonly currentPage: number;
  readonly onTurn: (page: number) => void;
  readonly onClose: () => void;
}): ReactNode {
  const entries = [
    ...(hasPreface ? [{ key: "title", label: CASEBOOK.titlePage, page: 0 as number | null }] : []),
    ...(hasPrologue ? [{ key: "prologue", label: CASEBOOK.prologue, page: 1 as number | null }] : []),
    ...chapters.map((chapter) => ({ key: chapter.key, label: chapter.label, page: pageOfKey(chapter.key) })),
  ];
  // The chapter being read: the last one that starts at or before the open page.
  const here = entries.reduce<string | null>((found, entry) => (entry.page !== null && entry.page <= currentPage ? entry.key : found), null);
  return (
    <div className="pcs-preface" data-region="contents" aria-label={CASEBOOK.contentsTitle}>
      <div className="pcs-preface-page pc-scroll">
        <p className="pcs-label pcs-book-eyebrow">{eyebrow}</p>
        <h2 className="pcs-story-title">{CASEBOOK.contentsTitle}</h2>
        <nav aria-label={CASEBOOK.contentsTitle}>
          <ol className="pcs-book-contents">
            {entries.map((entry) => (
              <li key={entry.key}>
                <button type="button" disabled={entry.page === null} aria-current={entry.key === here ? "page" : undefined} onClick={() => { if (entry.page !== null) onTurn(entry.page); }}>
                  <span>{entry.label}</span>
                  {entry.key === here ? <span className="pcs-book-here">{CASEBOOK.readingHere}</span> : null}
                </button>
              </li>
            ))}
          </ol>
        </nav>
        <button type="button" className="pcs-book-back" onClick={onClose}>{CASEBOOK.backToPage}</button>
      </div>
    </div>
  );
}

/**
 * A PAGE BEFORE THE STORY, drawn over the reading area on the panel's own
 * paper. Page one is the case's title page: the eyebrow, the client's name in
 * the storybook's light display face, the engagement question, and one
 * affordance. The pages after it are the authored prologue, rendered through
 * the same component the standalone prologue uses, so every block reaches the
 * page verbatim (beat.golden.test.tsx holds that promise for both).
 */
function PrefacePage({
  prologue,
  page,
  pager,
  onTurn,
  titleNote = null,
  titleEyebrow = PLAY.titleEyebrow,
}: {
  readonly prologue: PlayPrologue;
  readonly page: number;
  readonly pager: ReactNode;
  readonly onTurn: () => void;
  /** A closed book's title page carries how and when it ended (Codex: the date lives here, not in the running head). */
  readonly titleNote?: string | null;
  /** Over the title: the frames' line for a case, the arrival book's own for itself. */
  readonly titleEyebrow?: string;
}): ReactNode {
  const chapter = page === 0 ? null : (prologue.pages[page - 1] ?? null);
  return (
    <div className="pcs-preface" data-region="preface" data-preface-page={page === 0 ? "title" : "prologue"} aria-label={page === 0 ? "The title page" : "The prologue"}>
      <div className="pcs-preface-page pc-scroll">
        {chapter === null ? (
          <div className="pcs-title-page">
            <p className="pcs-label pcs-title-eyebrow">{titleEyebrow}</p>
            <h2 className="pcs-title-client">{prologue.client}</h2>
            <p className="pcs-title-industry">{prologue.industry}</p>
            {titleNote === null ? null : <p className="pcs-title-closed">{titleNote}</p>}
            {prologue.question.length > 0 ? <p className="pcs-title-question">{prologue.question}</p> : null}
            <button type="button" className="pcs-title-turn" onClick={onTurn}>
              {PLAY.turnThePage} <span aria-hidden="true">{PLAY.nextCaret}</span>
            </button>
          </div>
        ) : (
          <div className="pcs-prologue-page">
            <h2 className="pcs-story-title">
              {/* The running label, not the authored eyebrow (Codex, 2026-09-22):
                  "01 /" read as a sequence identifier. The authored blocks
                  below are untouched. */}
              <span className="pcs-prologue-eyebrow">{PLAY.prologueCaption} /</span>
              <br />
              {chapter.title}
            </h2>
            <div className="pcs-prologue-blocks">
              <PrologueBlocks blocks={chapter.blocks} pageKey={chapter.pageRef} />
            </div>
          </div>
        )}
      </div>
      {pager}
    </div>
  );
}

/**
 * The connection indicator, the resume confirmation, and the retry — the three
 * things that make an unreliable backend legible rather than mysterious.
 * Rendered in the panel's vocabulary: a hairline and small type. The one thing
 * allowed to draw the eye is the marker yellow behind `retry this turn.`
 */
function ConnectionLine({
  connection,
  resumedFromTurn,
  failure,
  onRetry,
}: {
  connection: ConnectionState;
  resumedFromTurn: number | null;
  failure: TurnFailure | null;
  onRetry: () => void;
}): ReactNode {
  const label = connection === "live" ? PROPOSED.connectionLive : connection === "reconnecting" ? PROPOSED.connectionReconnecting : PROPOSED.connectionLost;
  const nothingToSay = connection === "live" && failure === null && resumedFromTurn === null;

  if (nothingToSay) {
    return (
      <div className="mb-1 flex items-center gap-2 font-sans text-micro text-ink-faint">
        <span aria-hidden="true" className="inline-block h-[5px] w-[5px] rounded-pill bg-ink-faint" />
        <span>{label}</span>
      </div>
    );
  }

  return (
    <div className="mb-1 flex flex-wrap items-center gap-3 font-sans text-micro">
      <span className="flex items-center gap-2 text-ink-soft">
        <span
          aria-hidden="true"
          className="inline-block h-[5px] w-[5px] rounded-pill"
          style={{ background: connection === "live" ? "var(--pc-ink-faint)" : "var(--pc-ink)" }}
        />
        <span>{label}</span>
      </span>
      {resumedFromTurn !== null ? <span className="text-ink-soft">resumed from turn {resumedFromTurn}</span> : null}
      {failure !== null ? <span className="text-ink">{failure.message}</span> : null}
      {failure !== null ? (
        <button type="button" onClick={onRetry} className="cursor-pointer border-0 bg-marker p-0 px-1 font-sans text-micro text-ink">
          {PROPOSED.retryTurn}
        </button>
      ) : null}
    </div>
  );
}
